import { useEffect, useState } from 'react';
import { Scale, ArrowUpRight, ArrowDownRight, Sparkles, Loader2 } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { api } from '@/lib/api';
import { formatCurrency, formatNumber, formatPercent, pad2 } from '@/lib/utils';
import type { LiveDataEntity, LiveReviewEntity, AIAnalysisRequest } from '@shared/types';

export default function LiveComparisonPage() {
  const [liveData, setLiveData] = useState<LiveDataEntity[]>([]);
  const [a, setA] = useState<string>('');
  const [b, setB] = useState<string>('');
  const [reviews, setReviews] = useState<LiveReviewEntity[]>([]);
  const [aiReport, setAiReport] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    api.getLiveData().then((d) => {
      setLiveData(d);
      if (d.length >= 2) {
        setA(d[0].id);
        setB(d[1].id);
      } else if (d.length === 1) {
        setA(d[0].id);
        setB(d[0].id);
      }
    });
    api.getLiveReviews().then(setReviews);
  }, []);

  const liveA = liveData.find((x) => x.id === a);
  const liveB = liveData.find((x) => x.id === b);

  const handleAI = async () => {
    if (!liveA || !liveB) return;
    setAiLoading(true);
    try {
      const req: AIAnalysisRequest = {
        liveData: {
          views: liveA.viewCount,
          gmv: liveA.totalGmv,
          conversionRate: liveA.viewToConversionRate,
          interactRate: liveA.viewToInteractionRate,
          newFollowers: liveA.newFollowers,
          transactionCount: liveA.transactionCount
        },
        diagnosis: {
          overallScore: 80,
          overallHealth: 'good',
          issues: [],
          strengths: [{ title: '本场表现稳定' }]
        }
      };
      const res = await api.analyzeLiveReview(req);
      const lines = [
        `本次对比:${liveA.title} VS ${liveB.title}`,
        '',
        '【A 场表现】',
        `· GMV ${formatCurrency(liveA.totalGmv)} · 观看 ${formatNumber(liveA.viewCount)} · 转化 ${formatPercent(liveA.viewToConversionRate)}`,
        '',
        '【B 场表现】',
        `· GMV ${formatCurrency(liveB.totalGmv)} · 观看 ${formatNumber(liveB.viewCount)} · 转化 ${formatPercent(liveB.viewToConversionRate)}`,
        '',
        '【AI 洞察】',
        ...res.suggestions.map((s, i) => `${pad2(i + 1)} ${s.title}:${s.content}`)
      ];
      setAiReport(lines.join('\n'));
    } finally {
      setAiLoading(false);
    }
  };

  const radarOption = liveA && liveB ? {
    backgroundColor: 'transparent',
    legend: { textStyle: { color: '#aaa' }, top: 0 },
    radar: {
      indicator: [
        { name: 'GMV', max: 100 },
        { name: '观看', max: 100 },
        { name: '转化率', max: 100 },
        { name: '互动率', max: 100 },
        { name: '客单价', max: 100 }
      ],
      axisName: { color: '#aaa', fontSize: 11 },
      splitArea: { areaStyle: { color: ['#0a0a0d', '#131316'] } },
      splitLine: { lineStyle: { color: '#333' } },
      axisLine: { lineStyle: { color: '#333' } }
    },
    series: [{
      type: 'radar',
      data: [
        {
          name: liveA.title,
          value: [
            Math.min(100, (liveA.totalGmv / 1000000) * 100),
            Math.min(100, (liveA.viewCount / 50000) * 100),
            liveA.viewToConversionRate * 10,
            liveA.viewToInteractionRate * 10,
            Math.min(100, (liveA.totalGmv / Math.max(1, liveA.transactionCount)) / 5)
          ],
          lineStyle: { color: '#dafa1d', width: 2 },
          itemStyle: { color: '#dafa1d' },
          areaStyle: { color: 'rgba(218,250,29,0.2)' }
        },
        {
          name: liveB.title,
          value: [
            Math.min(100, (liveB.totalGmv / 1000000) * 100),
            Math.min(100, (liveB.viewCount / 50000) * 100),
            liveB.viewToConversionRate * 10,
            liveB.viewToInteractionRate * 10,
            Math.min(100, (liveB.totalGmv / Math.max(1, liveB.transactionCount)) / 5)
          ],
          lineStyle: { color: '#67e8f9', width: 2 },
          itemStyle: { color: '#67e8f9' },
          areaStyle: { color: 'rgba(103,232,249,0.2)' }
        }
      ]
    }]
  } : {};

  const metrics = liveA && liveB ? [
    { key: 'totalGmv', label: 'GMV', unit: '¥', a: liveA.totalGmv, b: liveB.totalGmv, format: formatCurrency },
    { key: 'viewCount', label: '观看人数', unit: '', a: liveA.viewCount, b: liveB.viewCount, format: formatNumber },
    { key: 'transactionCount', label: '订单数', unit: '', a: liveA.transactionCount, b: liveB.transactionCount, format: formatNumber },
    { key: 'viewToConversionRate', label: '转化率', unit: '%', a: liveA.viewToConversionRate, b: liveB.viewToConversionRate, format: (v: number) => v.toFixed(2) + '%' },
    { key: 'viewToInteractionRate', label: '互动率', unit: '%', a: liveA.viewToInteractionRate, b: liveB.viewToInteractionRate, format: (v: number) => v.toFixed(2) + '%' }
  ] : [];

  const barOption = liveA && liveB ? {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: '#000', borderColor: '#444', textStyle: { color: '#fff' } },
    legend: { textStyle: { color: '#aaa' }, top: 0 },
    grid: { top: 30, right: 24, bottom: 30, left: 80 },
    xAxis: { type: 'value', axisLine: { lineStyle: { color: '#444' } }, splitLine: { lineStyle: { color: '#222' } }, axisLabel: { color: '#aaa' } },
    yAxis: { type: 'category', data: metrics.map((m) => m.label), axisLine: { lineStyle: { color: '#444' } }, axisLabel: { color: '#aaa' } },
    series: [
      {
        name: liveA.title,
        type: 'bar',
        data: metrics.map((m) => m.a),
        itemStyle: { color: '#dafa1d' }
      },
      {
        name: liveB.title,
        type: 'bar',
        data: metrics.map((m) => m.b),
        itemStyle: { color: '#67e8f9' }
      }
    ]
  } : {};

  return (
    <div>
      <header className="border-b-2 border-border pb-8 mb-8">
        <div className="text-xs uppercase tracking-widest text-primary font-space mb-2">
          COMPARISON / {dayjs().format('YYYY.MM.DD')}
        </div>
        <h1 className="text-[clamp(3rem,12vw,8rem)] font-bold leading-[0.85] tracking-tighter uppercase font-space">
          数据<br />比对<span className="text-primary">.</span>
        </h1>
      </header>

      {/* 场次选择 */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-border mb-8">
        <div className="p-6 border-b-2 md:border-b-0 md:border-r-2 border-border">
          <label className="block text-xs uppercase tracking-widest font-bold text-primary mb-2">场次 A</label>
          <select
            value={a}
            onChange={(e) => setA(e.target.value)}
            className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary"
          >
            {liveData.map((l) => (
              <option key={l.id} value={l.id}>{l.title} - {l.date}</option>
            ))}
          </select>
        </div>
        <div className="p-6">
          <label className="block text-xs uppercase tracking-widest font-bold text-info mb-2">场次 B</label>
          <select
            value={b}
            onChange={(e) => setB(e.target.value)}
            className="w-full bg-input border-2 border-border px-4 py-3 outline-none focus:border-primary"
          >
            {liveData.map((l) => (
              <option key={l.id} value={l.id}>{l.title} - {l.date}</option>
            ))}
          </select>
        </div>
      </section>

      {!liveA || !liveB ? (
        <div className="border-2 border-border p-16 text-center text-muted-foreground">
          至少需要 2 场直播数据才能进行比对
        </div>
      ) : (
        <>
          {/* KPI 对比 */}
          <section className="grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-border mb-8">
            {metrics.map((m) => {
              const diff = m.b - m.a;
              const diffPercent = m.a !== 0 ? (diff / m.a) * 100 : 0;
              return (
                <div key={m.key} className="border-r-2 last:border-r-0 border-border p-4">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{m.label}</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-primary text-xs uppercase tracking-widest mb-1">A</div>
                      <div className="font-bold font-space tabular-nums">{m.format(m.a)}</div>
                    </div>
                    <div>
                      <div className="text-info text-xs uppercase tracking-widest mb-1">B</div>
                      <div className="font-bold font-space tabular-nums">{m.format(m.b)}</div>
                    </div>
                  </div>
                  <div className={`mt-2 flex items-center gap-1 text-xs font-bold ${
                    diff >= 0 ? 'text-primary' : 'text-destructive'
                  }`}>
                    {diff >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {diffPercent >= 0 ? '+' : ''}{diffPercent.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </section>

          {/* 雷达对比 */}
          <section className="border-2 border-border p-8 md:p-12 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-4 h-4 bg-primary" />
              <h2 className="text-2xl md:text-3xl font-bold font-space uppercase">多维度雷达对比</h2>
            </div>
            <ReactECharts option={radarOption} style={{ height: 400 }} />
          </section>

          {/* 柱状对比 */}
          <section className="border-2 border-border p-8 md:p-12 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-4 h-4 bg-primary" />
              <h2 className="text-2xl md:text-3xl font-bold font-space uppercase">核心指标柱状对比</h2>
            </div>
            <ReactECharts option={barOption} style={{ height: 300 }} />
          </section>

          {/* AI 对比分析 */}
          <section className="border-2 border-primary bg-primary text-black p-8 md:p-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6" />
                <h2 className="text-2xl md:text-3xl font-bold font-space uppercase">AI 对比分析</h2>
              </div>
              <button
                onClick={handleAI}
                disabled={aiLoading}
                className="bg-black text-primary px-4 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2 hover:bg-white disabled:opacity-50"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {aiReport ? '重新生成' : '生成报告'}
              </button>
            </div>
            {aiReport ? (
              <pre className="text-sm leading-relaxed whitespace-pre-wrap font-mono">{aiReport}</pre>
            ) : (
              <div className="text-sm">点击「生成报告」,由 Codex 规则引擎基于两场数据生成对比洞察。</div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
